import type { Question } from '@/types/question-types';
import { MultipleChoiceQuestion } from './MultipleChoiceQuestion';
import { FillBlankQuestion } from './FillBlankQuestion';
import { MixMatchQuestion } from './MixMatchQuestion';
import { MathQuestion } from './MathQuestion';
import { CodeQuestion } from './CodeQuestion';

// Placeholder components for future question types
import { ShortAnswerQuestionPlaceholder } from './ShortAnswerQuestion';
import { LongAnswerQuestionPlaceholder } from './LongAnswerQuestion';

interface QuestionRendererProps {
  question: Question;
  userAnswer?: unknown;
  onAnswer: (answer: unknown) => void;
  showExplanation?: boolean;
  isReviewMode?: boolean;
  mathLiveReady?: boolean;
}

/**
 * Main component that renders the appropriate question type component
 * based on the question's type field (discriminated union)
 */
export function QuestionRenderer({
  question,
  userAnswer,
  onAnswer,
  showExplanation = false,
  isReviewMode = false,
  mathLiveReady = false,
}: QuestionRendererProps) {
  // TypeScript's discriminated union provides type safety here
  switch (question.question_type) {
    case 'multiple_choice':
      return (
        <MultipleChoiceQuestion
          question={question}
          userAnswer={userAnswer as string[] | undefined}
          onAnswer={onAnswer}
          showExplanation={showExplanation}
          isReviewMode={isReviewMode}
        />
      );

    case 'fill_blank':
      return (
        <FillBlankQuestion
          question={question}
          userAnswer={userAnswer as Record<number, string> | undefined}
          onAnswer={onAnswer}
          showExplanation={showExplanation}
          isReviewMode={isReviewMode}
        />
      );

    case 'mix_match':
      return (
        <MixMatchQuestion
          question={question}
          userAnswer={
            userAnswer as
            | Array<{ leftId: string; rightId: string }>
            | undefined
          }
          onAnswer={onAnswer}
          showExplanation={showExplanation}
          isReviewMode={isReviewMode}
        />
      );

    case 'short_answer':
      return (
        <ShortAnswerQuestionPlaceholder
          question={question}
          userAnswer={userAnswer as string | undefined}
          onAnswer={onAnswer}
          showExplanation={showExplanation}
          isReviewMode={isReviewMode}
        />
      );

    case 'long_answer':
      return (
        <LongAnswerQuestionPlaceholder
          question={question}
          userAnswer={userAnswer as string | undefined}
          onAnswer={onAnswer}
          showExplanation={showExplanation}
          isReviewMode={isReviewMode}
        />
      );

    case 'math':
      return (
        <MathQuestion
          question={question}
          userAnswer={userAnswer as string | undefined}
          onAnswer={onAnswer}
          showExplanation={showExplanation}
          isReviewMode={isReviewMode}
          mathLiveReady={mathLiveReady}
        />
      );

    case 'code':
      return (
        <CodeQuestion
          question={question}
          userAnswer={userAnswer as string | undefined}
          onAnswer={onAnswer}
          showExplanation={showExplanation}
          isReviewMode={isReviewMode}
        />
      );

    default:
      // TypeScript ensures this is unreachable if all cases are handled
      // const _exhaustive: never = question;
      return (
        <div style={{ padding: '2rem', textAlign: 'center', color: 'red' }}>
          <strong>Error:</strong> Unknown question type
        </div>
      );
  }
}
